/**
 * ProgramRichForm — used by ESG Programas Ambientales, ESG Campañas e ISO 14001 Objetivos.
 * Provides a 3-section UI:
 *   1. Datos generales (single record)
 *   2. Sub-tabla dinámica de actividades (collection: activities, parent_id = program id)
 *   3. Evidencias generales del programa (FileCCE, module=collection)
 * Activities also have their own evidence dropzone (per-activity FileCCE module=activities).
 */
import { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import CrudTable from "./CrudTable";
import { Modal, FileCCE } from "./ui";
import { Plus, Trash2, GripVertical } from "lucide-react";

const STATUSES = ["Borrador", "En revisión", "Iniciado", "En proceso", "Completado", "Cancelado"];
const ACT_STATUSES = ["Pendiente", "En proceso", "Completado", "Cancelado"];

export default function ProgramRichForm({
  collection,           // e.g. "esg_programs", "iso_objectives", "esg_campaigns"
  testIdPrefix,
  title,
  extraFields = [],     // optional extra fields for the general data section
}) {
  const [selected, setSelected] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);

  async function createNew() {
    const name = window.prompt(`Nombre del ${title || "programa"}:`);
    if (!name) return;
    try {
      const { data } = await api.post(`/data/${collection}`, { name, status: "Borrador" });
      setSelected(data);
      setReloadKey((k) => k + 1);
    } catch (e) {
      alert("No se pudo crear el registro: " + (e.response?.data?.detail || e.message));
    }
  }

  return (
    <div data-testid={`${testIdPrefix}-rich`}>
      <div className="flex justify-end mb-2">
        <button className="btn btn-primary btn-sm" onClick={createNew} data-testid={`${testIdPrefix}-rich-new`}>
          <Plus size={12} /> Nuevo {title || "programa"}
        </button>
      </div>
      <CrudTable
        key={reloadKey}
        collection={collection}
        testIdPrefix={testIdPrefix}
        hideAdd
        onRowClick={(row) => setSelected(row)}
        fields={[
          { key: "name", label: "Nombre", required: true, full: true },
          { key: "leader", label: "Líder" },
          { key: "period", label: "Período" },
          { key: "status", label: "Status", type: "select", options: STATUSES },
          { key: "progress_pct", label: "% Avance", type: "number", render: (v) => `${Number(v || 0).toFixed(0)}%` },
        ]}
      />
      <Modal
        open={!!selected}
        onClose={() => { setSelected(null); setReloadKey((k) => k + 1); }}
        title={`${title || "Programa"} — ${selected?.name || ""}`}
        size="xl"
        testId={`${testIdPrefix}-detail`}
      >
        {selected && (
          <ProgramRichDetail
            collection={collection}
            testIdPrefix={testIdPrefix}
            program={selected}
            extraFields={extraFields}
            onClose={() => { setSelected(null); setReloadKey((k) => k + 1); }}
            onSaved={(updated) => setSelected(updated)}
          />
        )}
      </Modal>
    </div>
  );
}

function ProgramRichDetail({ collection, testIdPrefix, program, extraFields, onClose, onSaved }) {
  const [tab, setTab] = useState("data");
  const [form, setForm] = useState({ ...program });
  const [acts, setActs] = useState([]);
  const [saving, setSaving] = useState(false);

  async function loadActs() {
    try {
      const { data } = await api.get("/data/activities", { params: { program_id: program.id, program_collection: collection } });
      setActs((data || []).sort((a, b) => (a.order || 0) - (b.order || 0)));
    } catch (e) { console.error(e); }
  }
  useEffect(() => { loadActs(); /* eslint-disable-next-line */ }, [program.id]);

  // Auto-period from dates
  useEffect(() => {
    if (form.start_date && form.end_date && !form._period_manual) {
      const a = new Date(form.start_date), b = new Date(form.end_date);
      if (!isNaN(a) && !isNaN(b)) {
        const mes = (d) => d.toLocaleString("es-MX", { month: "short", year: "numeric" });
        setForm((f) => ({ ...f, period: `${mes(a)} → ${mes(b)}` }));
      }
    }
    /* eslint-disable-next-line */
  }, [form.start_date, form.end_date]);

  const completed = acts.filter((a) => a.status === "Completado").length;
  const progressPct = acts.length ? (completed / acts.length) * 100 : 0;

  async function saveGeneral() {
    setSaving(true);
    const payload = { ...form, progress_pct: progressPct };
    delete payload.id; delete payload.tenant_id; delete payload.created_at; delete payload.updated_at; delete payload._id;
    try {
      const { data } = await api.patch(`/data/${collection}/${program.id}`, payload);
      onSaved && onSaved(data);
      alert("Guardado.");
    } catch (e) {
      alert("Error al guardar: " + (e.response?.data?.detail || e.message));
    } finally { setSaving(false); }
  }

  async function addActivity() {
    const desc = window.prompt("Descripción de la actividad:");
    if (!desc) return;
    const order = acts.length;
    try {
      await api.post("/data/activities", {
        program_id: program.id,
        program_collection: collection,
        order,
        number: order + 1,
        description: desc,
        status: "Pendiente",
      });
      loadActs();
    } catch (e) { alert("Error: " + (e.response?.data?.detail || e.message)); }
  }

  async function updateActivity(act, field, value) {
    try {
      await api.patch(`/data/activities/${act.id}`, { [field]: value });
      setActs((arr) => arr.map((a) => a.id === act.id ? { ...a, [field]: value } : a));
    } catch (e) { console.error(e); }
  }

  async function delActivity(act) {
    if (!window.confirm("¿Eliminar esta actividad?")) return;
    try {
      await api.delete(`/data/activities/${act.id}`);
      loadActs();
    } catch (e) { alert("Error: " + e.message); }
  }

  // Drag to reorder
  const [dragIdx, setDragIdx] = useState(null);
  function onDragStart(i) { setDragIdx(i); }
  async function onDrop(i) {
    if (dragIdx == null || dragIdx === i) return;
    const arr = [...acts];
    const [m] = arr.splice(dragIdx, 1);
    arr.splice(i, 0, m);
    setActs(arr);
    setDragIdx(null);
    // Persist new order
    for (let idx = 0; idx < arr.length; idx++) {
      try { await api.patch(`/data/activities/${arr[idx].id}`, { order: idx, number: idx + 1 }); } catch { /* ignore */ }
    }
  }

  return (
    <div>
      <div className="tabs-bar">
        <button className={`tab ${tab === "data" ? "active" : ""}`} onClick={() => setTab("data")} data-testid={`${testIdPrefix}-tab-data`}>Datos generales</button>
        <button className={`tab ${tab === "activities" ? "active" : ""}`} onClick={() => setTab("activities")} data-testid={`${testIdPrefix}-tab-activities`}>Actividades ({acts.length})</button>
        <button className={`tab ${tab === "evidence" ? "active" : ""}`} onClick={() => setTab("evidence")} data-testid={`${testIdPrefix}-tab-evidence`}>Evidencias generales</button>
      </div>

      <div className="mb-3 flex items-center gap-3">
        <span className="text-xs text-dim">% Avance</span>
        <div style={{ background: "var(--bg-3)", height: 10, borderRadius: 4, overflow: "hidden", flex: 1 }}>
          <div style={{ width: `${progressPct}%`, height: "100%", background: progressPct >= 80 ? "var(--accent)" : progressPct >= 50 ? "var(--warn)" : "var(--danger)" }} />
        </div>
        <span className="text-sm font-semibold">{progressPct.toFixed(0)}%</span>
      </div>

      {tab === "data" && (
        <div className="grid grid-cols-2 gap-3">
          <RfField label="Nombre del programa *" v={form.name} on={(v) => setForm({ ...form, name: v })} testId={`${testIdPrefix}-name`} required />
          <RfField label="Líder" v={form.leader} on={(v) => setForm({ ...form, leader: v })} testId={`${testIdPrefix}-leader`} />
          <RfField label="Objetivo" type="textarea" v={form.objective} on={(v) => setForm({ ...form, objective: v })} testId={`${testIdPrefix}-objective`} full />
          <RfField label="Fecha inicio" type="date" v={form.start_date} on={(v) => setForm({ ...form, start_date: v, _period_manual: false })} testId={`${testIdPrefix}-start`} />
          <RfField label="Fecha fin" type="date" v={form.end_date} on={(v) => setForm({ ...form, end_date: v, _period_manual: false })} testId={`${testIdPrefix}-end`} />
          <RfField label="Período (auto, editable)" v={form.period} on={(v) => setForm({ ...form, period: v, _period_manual: true })} testId={`${testIdPrefix}-period`} />
          <RfField label="Status" type="select" options={STATUSES} v={form.status} on={(v) => setForm({ ...form, status: v })} testId={`${testIdPrefix}-status`} />
          <RfField label="# Revisión" type="number" v={form.revision} on={(v) => setForm({ ...form, revision: Number(v) || 0 })} testId={`${testIdPrefix}-rev`} />
          <RfField label="Aprobado por" v={form.approved_by} on={(v) => setForm({ ...form, approved_by: v })} testId={`${testIdPrefix}-approver`} />
          <RfField label="Observaciones" type="textarea" v={form.observations} on={(v) => setForm({ ...form, observations: v })} testId={`${testIdPrefix}-obs`} full />
          {extraFields.map((f) => (
            <RfField key={f.key} label={f.label} type={f.type} options={f.options} v={form[f.key]} on={(v) => setForm({ ...form, [f.key]: v })} testId={`${testIdPrefix}-${f.key}`} full={f.full} />
          ))}
          <div className="col-span-2 flex justify-end gap-2 mt-3">
            <button className="btn" onClick={onClose}>Cerrar</button>
            <button className="btn btn-primary" onClick={saveGeneral} disabled={saving} data-testid={`${testIdPrefix}-save-general`}>
              {saving ? <span className="spinner" /> : "Guardar"}
            </button>
          </div>
        </div>
      )}

      {tab === "activities" && (
        <div>
          <div className="flex items-center justify-between mb-2">
            <div className="text-xs text-dim">Arrastra el bloque para reordenar. Click en cualquier celda para editar.</div>
            <button className="btn btn-sm btn-primary" onClick={addActivity} data-testid={`${testIdPrefix}-add-act`}>
              <Plus size={12} /> Agregar actividad
            </button>
          </div>
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th style={{ width: 28 }}></th>
                  <th style={{ width: 50 }}>#</th>
                  <th>Descripción</th>
                  <th>Programada</th>
                  <th>Ejecutada</th>
                  <th>Status</th>
                  <th>Recursos</th>
                  <th>Rev #</th>
                  <th>Aprobador</th>
                  <th>Evidencias</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {acts.length === 0 ? (
                  <tr><td colSpan={11} className="text-dim text-center">Sin actividades</td></tr>
                ) : acts.map((a, i) => (
                  <tr key={a.id}
                    draggable
                    onDragStart={() => onDragStart(i)}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={() => onDrop(i)}
                  >
                    <td style={{ cursor: "grab" }}><GripVertical size={14} /></td>
                    <td>{i + 1}</td>
                    <td><input className="input" style={{ minWidth: 200 }} value={a.description ?? ""} onChange={(e) => setActs((arr) => arr.map((x) => x.id === a.id ? { ...x, description: e.target.value } : x))} onBlur={(e) => updateActivity(a, "description", e.target.value)} data-testid={`act-${i}-desc`} /></td>
                    <td><input className="input" style={{ width: 130 }} type="date" value={a.scheduled_date ?? ""} onChange={(e) => updateActivity(a, "scheduled_date", e.target.value)} /></td>
                    <td><input className="input" style={{ width: 130 }} type="date" value={a.executed_date ?? ""} onChange={(e) => updateActivity(a, "executed_date", e.target.value)} /></td>
                    <td>
                      <select className="select" value={a.status ?? "Pendiente"} onChange={(e) => updateActivity(a, "status", e.target.value)} data-testid={`act-${i}-status`}>
                        {ACT_STATUSES.map((s) => <option key={s}>{s}</option>)}
                      </select>
                    </td>
                    <td><input className="input" style={{ width: 130 }} value={a.resources ?? ""} onChange={(e) => setActs((arr) => arr.map((x) => x.id === a.id ? { ...x, resources: e.target.value } : x))} onBlur={(e) => updateActivity(a, "resources", e.target.value)} /></td>
                    <td><input className="input" style={{ width: 60 }} type="number" value={a.revision ?? 0} onChange={(e) => updateActivity(a, "revision", Number(e.target.value) || 0)} /></td>
                    <td><input className="input" style={{ width: 110 }} value={a.approved_by ?? ""} onChange={(e) => setActs((arr) => arr.map((x) => x.id === a.id ? { ...x, approved_by: e.target.value } : x))} onBlur={(e) => updateActivity(a, "approved_by", e.target.value)} /></td>
                    <td><ActivityEvidenceQuickLink act={a} /></td>
                    <td><button className="btn btn-sm btn-danger" onClick={() => delActivity(a)} title="Eliminar"><Trash2 size={12} /></button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === "evidence" && (
        <FileCCE module={collection} parentId={program.id} />
      )}
    </div>
  );
}

function ActivityEvidenceQuickLink({ act }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button className="btn btn-sm" onClick={() => setOpen(true)} data-testid={`act-${act.id}-evidence`}>Adjuntar</button>
      <Modal open={open} onClose={() => setOpen(false)} title={`Evidencias — ${act.description?.slice(0, 60) || act.id}`} size="lg">
        <FileCCE module="activities" parentId={act.id} />
      </Modal>
    </>
  );
}

function RfField({ label, type = "text", options, v, on, testId, full, required }) {
  return (
    <div style={full ? { gridColumn: "1 / -1" } : undefined}>
      <label className="label">{label}{required && " *"}</label>
      {type === "select" ? (
        <select className="select" value={v ?? ""} onChange={(e) => on(e.target.value)} data-testid={testId}>
          <option value="">--</option>
          {(options || []).map((o) => <option key={o}>{o}</option>)}
        </select>
      ) : type === "textarea" ? (
        <textarea className="textarea" rows={3} value={v ?? ""} onChange={(e) => on(e.target.value)} data-testid={testId} />
      ) : (
        <input className="input" type={type} value={v ?? ""} onChange={(e) => on(e.target.value)} data-testid={testId} />
      )}
    </div>
  );
}
